import { useCallback } from 'react';

import { error } from '@lumapps/notifications';
import { pin, unpin } from '@lumapps/posts/api';
import { REFRESH_EVENTS } from '@lumapps/posts/constants';
import { RefreshPosts } from '@lumapps/posts/types';
import { useDispatch } from '@lumapps/redux/react';
import { actions as spaceActions } from '@lumapps/spaces/ducks/slice';
import { SpacePinnedResourceType } from '@lumapps/spaces/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { useFetchWithStatus } from '@lumapps/utils/hooks/useFetchWithStatus';

import { PIN_POST_MAX_COUNT } from '../constants';
import { WIDGET_POST_LIST } from '../keys';
import { usePostListsRefresh } from './usePostListsRefresh';

export const usePostPin = (postId: string, communityId: string, refreshOverride?: RefreshPosts) => {
    const [isUnpinConfirmOpen, , closeUnpinConfirm, openUnpinConfirm] = useBooleanState(false);
    const { translateAndReplace } = useTranslate();
    const dispatch = useDispatch();

    const refreshPostLists = usePostListsRefresh(refreshOverride);
    const { fetch: performPin, status: pinStatus } = useFetchWithStatus({
        onFetch: () => pin(postId, communityId),
    });

    const { fetch: performUnpin, status: unpinStatus } = useFetchWithStatus({
        onFetch: () => unpin(postId, communityId),
    });

    const onUnpinPost = useCallback(() => {
        performUnpin({
            callback: (params) => {
                if (params.success && refreshPostLists) {
                    // Unpinning post from local Space if applicable
                    dispatch(spaceActions.unpinResource({ type: SpacePinnedResourceType.post, id: postId }));
                    refreshPostLists(REFRESH_EVENTS.POST_UNPIN, params.response);
                    closeUnpinConfirm();
                } else {
                    dispatch(error({ translate: GLOBAL.GENERIC_ERROR }));
                    closeUnpinConfirm();
                }
            },
        });
    }, [closeUnpinConfirm, dispatch, performUnpin, postId, refreshPostLists]);

    const onPinPost = useCallback(() => {
        performPin({
            callback: (params) => {
                if (params.success && refreshPostLists) {
                    // pinning post from local Space if applicable
                    dispatch(spaceActions.pinResource({ type: SpacePinnedResourceType.post, id: postId }));
                    refreshPostLists(REFRESH_EVENTS.POST_PIN, params.response);
                    // Status 413 means we exceeded the maximum number of pinned posts
                } else if (params.response?.status === 413) {
                    dispatch(
                        error({
                            translate: translateAndReplace(WIDGET_POST_LIST.PIN_POST_COUNT_EXCEEDED_ERROR, {
                                count: PIN_POST_MAX_COUNT,
                            }),
                        }),
                    );
                } else {
                    dispatch(error({ translate: GLOBAL.GENERIC_ERROR }));
                }
            },
        });
    }, [dispatch, performPin, postId, refreshPostLists, translateAndReplace]);

    const unpinConfirmDialogProps = {
        isOpen: isUnpinConfirmOpen,
        isLoading: unpinStatus === 'loading' || pinStatus === 'loading',
        onConfirm: onUnpinPost,
        onClose: closeUnpinConfirm,
    };

    return {
        unpinConfirmDialogProps,
        openUnpinConfirm,
        onPinPost,
    };
};
